import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import firebase from "firebase/app";
import { fires } from "../../firebase";

const toDate = (value) => {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleString() : "—";
};

const formatMoney = (value, currency = "CAD") => {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString(undefined, { style: "currency", currency })
    : "—";
};

export default function AdminLiveEventOrders() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [eventFilter, setEventFilter] = useState("all");
  const [eventSort, setEventSort] = useState("orders-desc");
  const [expandedEventIds, setExpandedEventIds] = useState(() => new Set());

  const loadLiveEvents = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const now = firebase.firestore.Timestamp.now();
      const eventSnapshot = await fires
        .collection("events")
        .where("status", "==", "live")
        .where("endDate", ">", now)
        .orderBy("endDate", "asc")
        .get();

      const eventRows = await Promise.all(
        eventSnapshot.docs.map(async (eventDoc) => {
          const [orderSnapshot, ticketSnapshot] = await Promise.all([
            fires
              .collection("orders")
              .where("eventId", "==", eventDoc.id)
              .get(),
            fires
              .collection("events")
              .doc(eventDoc.id)
              .collection("tickets")
              .get(),
          ]);

          const orders = orderSnapshot.docs
            .map((orderDoc) => ({ id: orderDoc.id, ...orderDoc.data() }))
            .sort((a, b) => {
              const aTime = toDate(a.timestamp)?.getTime() || 0;
              const bTime = toDate(b.timestamp)?.getTime() || 0;
              return bTime - aTime;
            });

          const ticketCount = ticketSnapshot.docs.filter(
            (ticketDoc) => ticketDoc.data()?.status !== "cancelled"
          ).length;

          return { id: eventDoc.id, ...eventDoc.data(), orders, ticketCount };
        })
      );

      setEvents(eventRows);
    } catch (err) {
      console.error("Unable to load live events and orders:", err);
      setError(
        err?.code === "failed-precondition"
          ? "This Firestore query needs a composite index for events: status (ascending) and endDate (ascending). Open the index link in the browser console error, create it, then refresh this page."
          : err?.message || "Unable to load live events and orders."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLiveEvents();
  }, [loadLiveEvents]);

  const filteredEvents = useMemo(
    () => events.filter((event) => eventFilter !== "one-or-more" || event.orders.length >= 1),
    [eventFilter, events]
  );

  const sortedEvents = useMemo(() => {
    const rows = [...filteredEvents];
    if (eventSort === "orders-asc") {
      return rows.sort((a, b) => a.orders.length - b.orders.length);
    }
    return rows.sort((a, b) => b.orders.length - a.orders.length);
  }, [eventSort, filteredEvents]);

  const totals = useMemo(() => {
    const orders = filteredEvents.flatMap((event) => event.orders);
    return {
      orderCount: orders.length,
      ticketCount: filteredEvents.reduce((sum, event) => sum + event.ticketCount, 0),
      gross: orders.reduce((sum, order) => sum + (Number(order.amount) || 0), 0),
    };
  }, [filteredEvents]);

  const toggleEventOrders = (eventId) => {
    setExpandedEventIds((current) => {
      const next = new Set(current);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedEventIds(new Set(sortedEvents.map((event) => event.id)));
  };

  const collapseAll = () => {
    setExpandedEventIds(new Set());
  };

  return (
    <Container fluid className="py-4 px-md-5" style={{ minHeight: "100vh", background: "#f6f7f9" }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <h1 className="h3 mb-1">Live event orders</h1>
          <div className="text-muted">Events with live status whose end date is still in the future.</div>
        </div>
        <div className="d-flex flex-wrap align-items-end gap-2">
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Filter events</Form.Label>
            <Form.Select
              value={eventFilter}
              onChange={(event) => setEventFilter(event.target.value)}
              aria-label="Filter live events by order count"
            >
              <option value="all">All live events</option>
              <option value="one-or-more">1 or more orders</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Order by order count</Form.Label>
            <Form.Select
              value={eventSort}
              onChange={(event) => setEventSort(event.target.value)}
              aria-label="Order events by order count"
            >
              <option value="orders-desc">Most orders first</option>
              <option value="orders-asc">Fewest orders first</option>
            </Form.Select>
          </Form.Group>
          <Button variant="outline-dark" onClick={loadLiveEvents} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="g-3 mb-4">
        <Col xs={12} md={3}>
          <Card className="h-100 border-0 shadow-sm"><Card.Body><div className="text-muted">Live events</div><div className="display-6 fw-bold">{filteredEvents.length}</div></Card.Body></Card>
        </Col>
        <Col xs={12} md={3}>
          <Card className="h-100 border-0 shadow-sm"><Card.Body><div className="text-muted">All orders</div><div className="display-6 fw-bold">{totals.orderCount}</div></Card.Body></Card>
        </Col>
        <Col xs={12} md={3}>
          <Card className="h-100 border-0 shadow-sm"><Card.Body><div className="text-muted">Active tickets</div><div className="display-6 fw-bold">{totals.ticketCount}</div><small className="text-muted">Cancelled excluded</small></Card.Body></Card>
        </Col>
        <Col xs={12} md={3}>
          <Card className="h-100 border-0 shadow-sm"><Card.Body><div className="text-muted">Gross order value</div><div className="display-6 fw-bold">{formatMoney(totals.gross)}</div></Card.Body></Card>
        </Col>
      </Row>

      {!loading && filteredEvents.length > 0 && (
        <div className="d-flex justify-content-end gap-2 mb-3">
          <Button size="sm" variant="outline-secondary" onClick={expandAll}>Expand all orders</Button>
          <Button size="sm" variant="outline-secondary" onClick={collapseAll}>Collapse all orders</Button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /><div className="mt-3">Loading events and orders…</div></div>
      ) : filteredEvents.length === 0 && !error ? (
        <Alert variant="info">
          {eventFilter === "one-or-more"
            ? "No live events have 1 or more orders."
            : "There are no live events with an end date after today."}
        </Alert>
      ) : (
        sortedEvents.map((event) => {
          const eventTotal = event.orders.reduce((sum, order) => sum + (Number(order.amount) || 0), 0);
          const isExpanded = expandedEventIds.has(event.id);
          return (
            <Card key={event.id} className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white py-3 d-flex flex-wrap justify-content-between gap-2">
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <h2 className="h5 mb-0">{event.title || event.eventTitle || "Untitled event"}</h2>
                    <Badge bg="success">live</Badge>
                  </div>
                  <small className="text-muted">Event ID: {event.id} · Ends {formatDate(event.endDate)}</small>
                </div>
                <div className="text-end d-flex align-items-center gap-3">
                  <div>
                  <div className="fw-bold">{event.orders.length} orders</div>
                  <small className="text-muted">{event.ticketCount} active tickets · {formatMoney(eventTotal)}</small>
                  </div>
                  <Button
                    size="sm"
                    variant="outline-dark"
                    onClick={() => toggleEventOrders(event.id)}
                    aria-expanded={isExpanded}
                    aria-controls={`event-orders-${event.id}`}
                  >
                    {isExpanded ? "Hide orders" : "Show orders"}
                  </Button>
                </div>
              </Card.Header>
              {isExpanded && <Card.Body id={`event-orders-${event.id}`} className="p-0">
                {event.orders.length === 0 ? (
                  <div className="p-4 text-muted">No orders for this event.</div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="table-light">
                        <tr><th>Order</th><th>Customer</th><th>Date</th><th>Status</th><th>Items</th><th className="text-end">Amount</th></tr>
                      </thead>
                      <tbody>
                        {event.orders.map((order) => (
                          <tr key={order.id}>
                            <td><Link to={`/admin-order-details/${order.id}`}>{order.id}</Link></td>
                            <td><div>{order.userName || "—"}</div><small className="text-muted">{order.userEmail || "—"}</small></td>
                            <td>{formatDate(order.timestamp)}</td>
                            <td><Badge bg={order.status === "completed" ? "success" : "secondary"}>{order.status || "unknown"}</Badge></td>
                            <td>{Array.isArray(order.cartItems) ? order.cartItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0) : 0}</td>
                            <td className="text-end fw-semibold">{formatMoney(order.amount, order.currency || "CAD")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>}
            </Card>
          );
        })
      )}
    </Container>
  );
}
